import React, { useEffect, useMemo, useState } from "react";

export type NotificationItem = {
  id: string;
  title: string;
  body: string;
  receivedAt: string;
  read: boolean;
  data?: Record<string, unknown>;
};

type NotificationBellProps = {
  notifications?: NotificationItem[];
  unreadCount?: number;
  onMarkAsRead?: () => void;
  onClear?: () => void;
};

const bellWrapStyle: React.CSSProperties = {
  position: "relative",
};

const bellButtonStyle: React.CSSProperties = {
  position: "relative",
  width: "46px",
  height: "46px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: "18px",
};

const badgeStyle: React.CSSProperties = {
  position: "absolute",
  top: "-6px",
  right: "-6px",
  minWidth: "20px",
  height: "20px",
  padding: "0 6px",
  borderRadius: "999px",
  background: "var(--primary)",
  color: "#ffffff",
  border: "2px solid var(--surface)",
  fontSize: "11px",
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const panelStyle: React.CSSProperties = {
  position: "absolute",
  top: "64px",
  right: 0,
  width: "360px",
  maxWidth: "calc(100vw - 32px)",
  borderRadius: "20px",
  border: "1px solid var(--border)",
  background: "var(--background)",
  color: "var(--text-primary)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
  overflow: "hidden",
  zIndex: 50,
};

const panelHeaderStyle: React.CSSProperties = {
  padding: "16px",
  borderBottom: "1px solid var(--border)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
};

const panelTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "15px",
  fontWeight: 800,
};

const panelActionStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--text-secondary)",
  fontSize: "12px",
  fontWeight: 700,
  cursor: "pointer",
};

const listStyle: React.CSSProperties = {
  maxHeight: "380px",
  overflowY: "auto",
};

const itemStyle: React.CSSProperties = {
  padding: "14px 16px",
  borderBottom: "1px solid var(--border)",
};

const itemUnreadStyle: React.CSSProperties = {
  background: "var(--primary-soft)",
  borderLeft: "3px solid var(--primary)",
};

const itemTitleStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 800,
  marginBottom: "6px",
};

const itemBodyStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  lineHeight: 1.45,
};

const itemDateStyle: React.CSSProperties = {
  marginTop: "8px",
  fontSize: "11px",
  color: "var(--text-secondary)",
};

const emptyStyle: React.CSSProperties = {
  padding: "24px 16px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  textAlign: "center",
};

const formatNotificationDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const NotificationBell = ({
  notifications = [],
  unreadCount = 0,
  onMarkAsRead,
  onClear,
}: NotificationBellProps) => {
  const [open, setOpen] = useState(false);

  const visibleNotifications = useMemo(
    () => notifications.slice(0, 50),
    [notifications]
  );

  useEffect(() => {
    if (open) {
      onMarkAsRead?.();
    }
  }, [open, onMarkAsRead]);

  return (
    <div style={bellWrapStyle}>
      <button
        type="button"
        style={bellButtonStyle}
        onClick={() => setOpen((previous) => !previous)}
        title="Notificaciones"
        aria-label="Notificaciones"
        aria-expanded={open}
      >
        🔔

        {unreadCount > 0 ? (
          <span style={badgeStyle}>
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>
            <h3 style={panelTitleStyle}>Notificaciones</h3>

            {visibleNotifications.length > 0 ? (
              <button
                type="button"
                style={panelActionStyle}
                onClick={onClear}
              >
                Limpiar
              </button>
            ) : null}
          </div>

          <div style={listStyle}>
            {visibleNotifications.length > 0 ? (
              visibleNotifications.map((notification) => (
                <div
                  key={notification.id}
                  style={{
                    ...itemStyle,
                    ...(!notification.read ? itemUnreadStyle : {}),
                  }}
                >
                  <div style={itemTitleStyle}>{notification.title}</div>
                  <div style={itemBodyStyle}>{notification.body}</div>
                  <div style={itemDateStyle}>
                    {formatNotificationDate(notification.receivedAt)}
                  </div>
                </div>
              ))
            ) : (
              <div style={emptyStyle}>
                Todavía no hay notificaciones.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationBell;